/* eslint-disable react/prop-types */
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Eye, Trash2, PenLine, Info, Archive, ArchiveRestore } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import {
  DeleteConfirmDialog,
  ArchiveConfirmDialog,
} from "@/components/dialog/common-dialog";
import MixAndMatchDetailsDialog from "@/components/dialog/menu-manage/mix-and-match/mix-and-match-details-dialog";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import {
  deleteMixAndMatchById,
  clearMixAndMatchMessages,
  updateExistingMixAndMatch,
  toggleMixAndMatchStatusAction,
  fetchAllMixAndMatches,
} from "@/redux/slice/menu-manage-slice/mix-and-match-slice";
import hasPermission from "@/hooks/hasPermission";
import { Switch } from "@/components/ui/switch";
import useAuth from "@/hooks/useAuth";

function MixAndMatchListTable({ mixAndMatches }) {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const { deleteLoading, updateLoading } = useSelector(
    (state) => state.menuManage.mixAndMatch
  );

  const [archiveDialog, setArchiveDialog] = useState(false);
  const [archiveActionType, setArchiveActionType] = useState("archive");
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);

  const closeArchiveDialog = () => {
    setArchiveDialog(false);
    setSelectedItem(null);
  };

  const archiveHandler = async (id, item, type) => {
    try {
      if (!id) {
        toast.error("ID is required.");
        return;
      }
      dispatch(clearMixAndMatchMessages());
      const newStatus = type === "archive" ? "archived" : "active";
      const res = await dispatch(
        updateExistingMixAndMatch({
          id,
          data: {
            ...item,
            status: newStatus,
          },
        })
      );
      if (updateExistingMixAndMatch.fulfilled.match(res)) {
        toast.success(`Mix & Match ${type === "archive" ? "archived" : "restored"} successfully!`);
      } else if (updateExistingMixAndMatch.rejected.match(res)) {
        toast.error(res?.payload || `Failed to update status.`);
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    } finally {
      closeArchiveDialog();
    }
  };

  const deleteHandler = async (id) => {
    try {
      if (!id) {
        toast.error("ID is required.");
        return;
      }
      dispatch(clearMixAndMatchMessages());
      const res = await dispatch(deleteMixAndMatchById(id));
      if (deleteMixAndMatchById.fulfilled.match(res)) {
        toast.success(`Mix & Match deleted successfully!`);
      } else if (deleteMixAndMatchById.rejected.match(res)) {
        toast.error(res?.payload || `Failed to delete Mix & Match.`);
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    } finally {
      setDeleteDialog(false);
      setSelectedItem(null);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      dispatch(clearMixAndMatchMessages());
      const res = await dispatch(toggleMixAndMatchStatusAction(id));
      if (toggleMixAndMatchStatusAction.fulfilled.match(res)) {
        toast.success("Mix & Match status updated successfully!");
      } else {
        toast.error(res?.payload || "Failed to toggle status.");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    }
  };

  return (
    <div className="w-full overflow-x-auto rounded-lg border border-cblack6 bg-cblack2">
      <Table className="w-full">
        <TableHeader>
          <TableRow className="border-b border-cblack6 hover:bg-transparent">
            <TableHead className="w-[80px]">ID</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Max Choose</TableHead>
            <TableHead>Categories</TableHead>
            <TableHead>VAT (%)</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {mixAndMatches && mixAndMatches.length > 0 ? (
            mixAndMatches.map((item) => (
              <TableRow
                key={item?.id}
                className="border-b border-cblack6 hover:bg-cblack3 transition-colors"
              >
                <TableCell className="font-medium text-cwhite">
                  #{item?.id}
                </TableCell>
                <TableCell className="font-semibold text-cwhite">
                  {item?.name}
                </TableCell>
                <TableCell>
                  <span className="px-2.5 py-1 rounded bg-cblack7 text-cyellow text-xs font-semibold">
                    {item?.max_choose ?? "N/A"}
                  </span>
                </TableCell>
                <TableCell>
                  <span
                    onClick={() => {
                      setSelectedItem(item);
                      setDetailsDialog(true);
                    }}
                    className="flex gap-1.5 items-center cursor-pointer text-cwhite2 hover:text-cyellow transition-colors"
                  >
                    <span>{item?.categories?.length || item?.categories_count || 0} Categories</span>
                    <Eye size={16} className="text-cyellow" />
                  </span>
                </TableCell>
                <TableCell className="text-cwhite2">
                  {item?.vat ?? 0}%
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {item?.status === "active" ? (
                      <>
                        <span className="text-cgreen text-sm">Active</span>
                        <Switch
                          checked={true}
                          onCheckedChange={() => handleToggleStatus(item.id)}
                        />
                      </>
                    ) : item?.status === "inactive" ? (
                      <>
                        <span className="text-cred text-sm">Inactive</span>
                        <Switch
                          checked={false}
                          onCheckedChange={() => handleToggleStatus(item.id)}
                        />
                      </>
                    ) : item?.status === "archived" ? (
                      <span className="text-amber-500 text-sm font-medium">Archived</span>
                    ) : (
                      <span className="text-cwhite3 text-sm font-medium">Draft</span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2.5">
                    <button
                      type="button"
                      title="View Details"
                      onClick={() => {
                        setSelectedItem(item);
                        setDetailsDialog(true);
                      }}
                      className="text-cwhite3 hover:text-cwhite transition-colors"
                    >
                      <Info size={18} />
                    </button>

                    {hasPermission("back_office.menu_management.update_mix_match") && item?.status !== "archived" && (
                      <Link
                        to={"/backoffice/menu/mix-and-match/update"}
                        state={item}
                        title="Edit"
                        className="text-cyellow hover:opacity-80 transition-opacity"
                      >
                        <PenLine size={18} />
                      </Link>
                    )}

                    {hasPermission("back_office.menu_management.delete_mix_match") && (
                      item?.status === "draft" ? (
                        <button
                          type="button"
                          title="Delete"
                          onClick={() => {
                            setSelectedItem(item);
                            setDeleteDialog(true);
                          }}
                          className="text-cred hover:opacity-80 transition-opacity"
                        >
                          <Trash2 size={18} />
                        </button>
                      ) : item?.status === "archived" ? (
                        <button
                          type="button"
                          title="Restore"
                          onClick={() => {
                            setSelectedItem(item);
                            setArchiveActionType("restore");
                            setArchiveDialog(true);
                          }}
                          className="text-cgreen hover:opacity-80 transition-opacity"
                        >
                          <ArchiveRestore size={18} />
                        </button>
                      ) : (
                        <button
                          type="button"
                          title="Archive"
                          onClick={() => {
                            setSelectedItem(item);
                            setArchiveActionType("archive");
                            setArchiveDialog(true);
                          }}
                          className="text-amber-500 hover:opacity-80 transition-opacity"
                        >
                          <Archive size={18} />
                        </button>
                      )
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-8 text-cwhite3">
                No Mix & Match items available.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Dialogs */}
      <MixAndMatchDetailsDialog
        isOpen={detailsDialog}
        onClose={() => {
          setDetailsDialog(false);
          setSelectedItem(null);
        }}
        item={selectedItem}
      />

      <ArchiveConfirmDialog
        isLoading={updateLoading}
        isOpen={archiveDialog}
        onClose={closeArchiveDialog}
        onConfirm={() => {
          archiveHandler(selectedItem?.id, selectedItem, archiveActionType);
        }}
        title={archiveActionType === "archive" ? "Archive Mix & Match?" : "Restore Mix & Match?"}
        description={
          archiveActionType === "archive"
            ? `Are you sure you want to archive "${selectedItem?.name}"?`
            : `Are you sure you want to restore "${selectedItem?.name}"?`
        }
        isArchive={archiveActionType === "archive"}
      />

      <DeleteConfirmDialog
        isLoading={deleteLoading}
        isOpen={deleteDialog}
        onClose={() => {
          setDeleteDialog(false);
          setSelectedItem(null);
        }}
        onConfirm={() => {
          deleteHandler(selectedItem?.id);
        }}
        title="Delete Mix & Match?"
        description={`Are you sure you want to delete "${selectedItem?.name}"? This action cannot be undone.`}
      />
    </div>
  );
}

export default MixAndMatchListTable;
